import React, { useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import StepConnector from "@material-ui/core/StepConnector";
import Typography from "@material-ui/core/Typography";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useStudyPlan } from "../../../app/contexts/study.context";
import { useUser } from "../../../app/contexts/user.context";
import { useSnackbar } from "../../../app/contexts/snackbar.context";
import UnsavedChangesModal from "../unsaved.changes.modal";

const ColorlibConnector = withStyles((theme) => ({
  alternativeLabel: {
    top: 22,
  },
  active: {
    "& $line": {
      background: "var(--teal-600)",
    },
  },
  completed: {
    "& $line": {
      background: "var(--teal-600)",
    },
  },
  disabled: {
    "& $line": {
      background: "var(--surface-300)",
    },
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor: "var(--surface-300)",
    borderRadius: 1,
  },
}))(StepConnector);

const useColorlibStepIconStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "var(--surface-300)",
    zIndex: 1,
    color: "var(--White)",
    width: 50,
    height: 50,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
  },
  active: {
    backgroundColor: "var(--teal-600)",
    boxShadow: "none",
  },
  completed: {
    backgroundColor: "var(--teal-600)",
    boxShadow: "none",
  },
}));

const useStepperStyles = makeStyles(() => ({
  readOnlyIcon: {
    backgroundColor: "#85B9B9 !important",
    pointerEvents: "none !important",
  },
}));

function ColorlibStepIcon(props) {
  const classes = useColorlibStepIconStyles();
  const stepperClasses = useStepperStyles();
  const { isReadOnlyUser } = useUser();

  const { active, completed } = props;

  const icons = {
    1: <Typography variant="h5">1</Typography>,
    2: <Typography variant="h5">2</Typography>,
    3: <Typography variant="h5">3</Typography>,
  };

  // Apply muted colors to icons 1 and 2 when isReadOnlyUser is true
  const shouldBeMuted = isReadOnlyUser && (props.icon === 1 || props.icon === 2);

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active && !shouldBeMuted,
        [classes.completed]: completed && !shouldBeMuted,
        [stepperClasses.readOnlyIcon]: shouldBeMuted,
      })}
    >
      {icons[String(props.icon)]}
    </div>
  );
}

export default function CustomizedSteppers({ errors, warnings }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, planId } = useParams();
  const [open, setOpen] = useState(false);
  const [routeTo, setRoutTo] = useState("");
  const { studyPlan, saveLoading, unsavedChange } = useStudyPlan();
  const { isReadOnlyUser } = useUser();
  const { showSnackbar } = useSnackbar();
  const {
    studyPlanName,
    croProjectStartDateMillis,
    studyPlanDescr,
    finalProtocolStartDateMillis,
    studyPlanId,
    //treatmentDuration,
    //screeningPeriodDuration,
  } = studyPlan;

  const handleDisabled = () =>
    !studyPlanName ||
    !croProjectStartDateMillis ||
    !studyPlanDescr ||
    !finalProtocolStartDateMillis ||
    //!subjects_to_screen.lowerBound > 0 ||
    !studyPlanId;

  const handleActiveStep = () => {
    if (location.pathname.includes("review")) {
      return 2;
    } else if (
      location.pathname.includes("countries-and-site-groups")
    ) {
      return 1;
    } else {
      return 0;
    }
  };

  const steps = [
    {
      label: "Overall Parameters",
      to: `/ctpo/study-plans/${id}/${planId}`,
      step: 1,
    },
    {
      label: "Countries & Site Groups",
      to: `/ctpo/study-plans/${id}/${planId}/countries-and-site-groups`,
      step: 2,
    },
    {
      label: "Review Study Plan",
      to: `/ctpo/study-plans/${id}/${planId}/review`,
      step: 3,
    },
  ];

  const handleClick = (to, step) => {
    studyPlan["ActualStep"] = step;
    if (handleDisabled()) {
      return;
    }
    if (unsavedChange) {
      if (Object.keys(warnings || {}).length !== 0) {
        showSnackbar(
          <ul>
            {Object.keys(warnings || {}).map((key) => {
              if (key !== "BUTTON_TYPE") return <li>{warnings[key]}</li>;
              return null;
            })}
          </ul>,
          "warning",
        );
      }

      if (Object.keys(errors || {}).length !== 0 &&
        (["ALL"].includes(errors.BUTTON_TYPE) || to?.includes("review"))
      ) {
        showSnackbar(
          <ul>
            {Object.keys(errors || {}).map((key) => {
              if (key === "ENROLLMENT_DURATION" && !to?.includes("review"))
                return "";
              if (key !== "BUTTON_TYPE") return <li>{errors[key]}</li>;
              return "";
            })}
          </ul>,
          "error",
        );
        navigate(to);
        return;
      }
      setOpen(true);
    } else {
      navigate(to);
    }
  };

  return (
    <Stepper
      alternativeLabel
      activeStep={handleActiveStep()}
      connector={<ColorlibConnector />}
    >
      {steps.map(({ label, to, step }) => (
        <Step
          key={label}
          onClick={() => {
            if (isReadOnlyUser) return; // Prevent clicks when in read-only mode
            handleClick(to, step);
            setRoutTo(to);
          }}
          disabled={saveLoading || isReadOnlyUser}
        >
          <StepLabel StepIconComponent={ColorlibStepIcon}>
            <strong>{label}</strong>
          </StepLabel>
        </Step>
      ))}

      <UnsavedChangesModal
        open={open}
        onToggle={(prev) => setOpen(!prev)}
        to={routeTo}
      />
    </Stepper>
  );
}
